// my-app/src/stores/authStore.ts
import { defineStore } from 'pinia';
import { login, register, initCaptcha, logout } from '@/api/auth';
import type { LoginCredentials, RegisterCredentials } from '@/types/auth';
import { logger } from '@myorg/type-lib';
import api from '@/services/api';

// 使用 username 作為判斷關鍵字
const USERNAME_KEY = 'username';

const getInitialUsername = () => {
  return localStorage.getItem(USERNAME_KEY) || '';
};

export const useAuthStore = defineStore('auth', {
  state: () => ({
    // 只要有 username 就視為初步登入（適合做 UI 導向判斷）
    isLoggedIn: !!getInitialUsername(),
    error: '',
    message: '',
  }),

  actions: {
    // 🚀 更新登入狀態與使用者資訊
    setLoginStatus(status: boolean) {
      if (status) {
        this.isLoggedIn = true;
      } else {
        this.isLoggedIn = false;
        // 修正：抽離出專門的憑證清除邏輯，拒絕使用 localStorage.clear() 誤殺主題或語系
        this.clearAuthData();
      }
    },

    // 🧹 專門清除驗證資料的私有輔助函式
    clearAuthData() {
      localStorage.removeItem(USERNAME_KEY);
      localStorage.clear()
      // 如果你未來有存其他像是 'user_info' 或 'permissions'，也在這裡一併 removeItem 掉
    },

    async checkAuthStatus() {
      try {
        // 呼叫 bff-client 的 /me 端點檢查 OAuth 登入狀態
        const res = await api().get('/api/client/me');
        if (res.data && res.data.authenticated) {
          const user = res.data;
          localStorage.setItem(USERNAME_KEY, user.name || user.email || user.sub);
          localStorage.setItem('userRoles', JSON.stringify(user.roles || []));
          this.setLoginStatus(true);
          return true;
        }
      } catch (err) {
        logger.log("checkAuthStatus err", err);
      }
      this.setLoginStatus(false);
      return false;
    },

    async handleLogin(data: LoginCredentials & { rememberMe: boolean }) {
      // 改用 OAuth 登入，直接導向 bff-client 的登入端點
      // window.location.href = '/api/client/login';
      this.error = '';
      this.message = '';
      logger.log("handleLogin data", data);
      try {
        const res = await login(data);
        logger.log("handleLogin res", res);
        this.message = '登入成功';
        return res;
      } catch (err: any) {
        this.error = err.response?.data?.message || '登入失敗';
        throw err;
      }
    },

    async handleRegister(data: RegisterCredentials) {
      this.error = '';
      this.message = '';
      try {
        await register(data);
        this.message = '註冊成功';
      } catch (err: any) {
        this.error = err.response?.data?.message || '註冊失敗';
        throw err;
      }
    },

    async initCaptcha() {
      try {
        return await initCaptcha();
      } catch (err) {
        this.error = '驗證碼初始化失敗';
        throw err;
      }
    },

    async logout() {
      try {
        // 改用 OAuth 登出
        await logout();
        // window.location.href = '/api/client/logout';
      } finally {
        this.setLoginStatus(false);
        this.error = '';
        this.message = '您已登出';
      }
    },

    // 💡 這是最關鍵的安全防線
    // 當後端發現 Cookie 過期回傳 401，Interceptor 呼叫此方法
    forceLogoutByApi() {
      logger.log("forceLogoutByApi triggered by interceptor");
      
      // 1. 先執行狀態與資料清除
      this.setLoginStatus(false);
      
      // 2. 覆蓋掉原本的錯誤訊息，給使用者明確的提示
      this.error = '登入逾時，請重新登入';
      this.message = '';
    }
  },
});